import withParams from "@src/components/widgets/HOC/withParams";
import React, { useCallback, useMemo } from "react";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetInnovationPetitPrinceParams } from "../interface";
import LandingInnovationPetitPrince, {
  ILandingInnovationPetitPrinceProduct,
} from "design-system/src/Components/WidgetPart/LandingInnovation/LandingInnovationPetitPrince/LandingInnovationPetitPrince";
import { InnovationProductAdditionalData } from "@src/components/widgets/hooks/useProductList";
import { useTranslation } from "@src/hooks/useTranslation";
import InnovationProductPetitPrince, {
  IInnovationProductPetitPrinceProps,
} from "design-system/src/Components/WidgetPart/LandingInnovation/LandingInnovationPetitPrince/InnovationProductPetitPrince/InnovationProductPetitPrince";
import useCtaAction from "@src/components/widgets/hooks/useCtaAction";
import useBackboneContext from "@src/backbone/provider/useBackboneContext";
import { lePetitPrinceImages } from "design-system/src/Assets/images/le-petit-prince";
import { useAllProduct } from "@src/hooks/query/useAllProduct";
import { defaultTheme } from "design-system/src/Themes/defaultTheme";

// Default values for each product: image, color, descriptionColor
const productDefaults: {
  image: string;
  color: string;
  descriptionColor: string;
  compactComposition?: boolean;
}[] = [
  {
    image: lePetitPrinceImages.products.sushiEtoile,
    color: defaultTheme.color.petitPrinceBrown,
    descriptionColor: defaultTheme.color.petitPrinceBrown,
  },
  {
    image: lePetitPrinceImages.products.makiPrince,
    color: defaultTheme.color.petitPrinceGreen,
    descriptionColor: "#122A1C", // maki prince description color
  },
  {
    image: lePetitPrinceImages.products.californiaRose,
    color: defaultTheme.color.petitPrinceRed,
    descriptionColor: defaultTheme.color.petitPrinceRed,
    compactComposition: true,
  },
  {
    image: lePetitPrinceImages.products.springRenard,
    color: defaultTheme.color.petitPrinceOrange,
    descriptionColor: defaultTheme.color.petitPrinceOrange,
  },
];
// Wrapper component that isolates useCtaAction to prevent re-renders of other products
const ProductItemWithHandler = React.memo(
  (props: IInnovationProductPetitPrinceProps<InnovationProductAdditionalData> & { index: number }) => {
    const { product: flatProduct, index, ...rest } = props;
    const { id_product, actionType, link } = flatProduct;
    const { t } = useTranslation(["landing"]);
    const { generateClick, getSubmitLabel } = useCtaAction(id_product || "");

    const clickHandler = useMemo(() => {
      return generateClick(
        actionType && actionType === "open_link"
          ? { type: "open_link", link: link || "#" }
          : { type: actionType || "product_card" }
      );
    }, [generateClick, actionType, link]);

    const handleClick = useCallback(() => {
      clickHandler();
    }, [clickHandler]);

    const ctaLabel = useMemo(
      () => getSubmitLabel(false, t("Je commande", "landing")),
      [getSubmitLabel, t]
    );

    const productWithLabel = useMemo(
      () => ({
        ...flatProduct,
        ctaLabel,
      }),
      [flatProduct, ctaLabel]
    );

    return (
      <InnovationProductPetitPrince
        {...rest}
        product={productWithLabel}
        onClick={handleClick}
      />
    );
  },
  (prevProps, nextProps) => {
    // Strict comparison: only re-render if product data actually changes
    // This prevents re-renders when cart changes (which updates useCtaAction)
    const prevProduct = prevProps.product;
    const nextProduct = nextProps.product;
    
    // Compare all product properties that affect rendering
    // Note: We compare the original product data, not the productWithLabel
    // because ctaLabel can change when cart changes, but we don't want to re-render
    // the entire component (especially images) just because the label changed
    const productDataEqual =
      prevProduct.id_product === nextProduct.id_product &&
      prevProduct.imageId === nextProduct.imageId &&
      prevProduct.staticImageSrc === nextProduct.staticImageSrc &&
      prevProduct.title === nextProduct.title &&
      prevProduct.description === nextProduct.description &&
      prevProduct.composition === nextProduct.composition &&
      prevProduct.price === nextProduct.price &&
      prevProduct.priceSubtitle === nextProduct.priceSubtitle &&
      prevProduct.color === nextProduct.color &&
      prevProduct.descriptionColor === nextProduct.descriptionColor &&
      prevProduct.actionType === nextProduct.actionType &&
      prevProduct.link === nextProduct.link &&
      prevProduct.displayFromMention === nextProduct.displayFromMention &&
      prevProduct.displayPrice === nextProduct.displayPrice &&
      prevProduct.compactComposition === nextProduct.compactComposition;

    // Only re-render if product data changed OR className/index changed
    // Ignore ctaLabel changes to prevent image re-renders
    return (
      productDataEqual &&
      prevProps.className === nextProps.className &&
      prevProps.index === nextProps.index
    );
  }
);

const InnovationPetitPrince: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetInnovationPetitPrinceParams>((props) => {
    const { params } = props;
    const { app } = useBackboneContext();
    const { data: products } = useAllProduct();

    // Memoize product IDs to avoid unnecessary recalculations
    const productIds = useMemo(() => {
      const _params: Record<string, string | undefined> = params as any;
      return Array.from({ length: 4 }, (value, index) => index + 1)
        .map((key) => _params[`product_${key}`])
        .filter(Boolean) as string[];
    }, [params]);

    // Create a stable reference for products data
    // Only recalculate if productIds or products actually change
    const productsDataStable = useMemo(() => {
      // Only use products data that we actually need, and memoize it
      const dataMap = new Map();
      productIds.forEach((id) => {
        const product = products.find(
          (p) => !!(p.id_product && p.id_product === id)
        );
        if (product) {
          dataMap.set(id, {
            id_product: product.id_product,
            description: product.description,
          });
        }
      });
      return dataMap;
    }, [productIds, products]);

    const preparePetitPrinceProductList = useMemo(() => {
      const _list: ILandingInnovationPetitPrinceProduct<InnovationProductAdditionalData>[] =
        [];
      const _params: Record<string, string | undefined> = params as any;

      // Process only first 4 products
      Array.from({ length: 4 }, (value, index) => index + 1).forEach((key) => {
        const id_product = _params[`product_${key}`];
        const name_product = _params[`name_product_${key}`];
        const description_product = _params[`description_product_${key}`];
        const composition_product = _params[`composition_product_${key}`];
        const image_product = _params[`image_product_${key}`];
        const color = _params[`color_product_${key}`];
        const descriptionColor = _params[`description_color_product_${key}`];
        const actionType = _params[`action_product_${key}`] as any;
        const link = _params[`link_product_${key}`];
        const displayFromMentionRaw =
          _params[`product_${key}_display_from_mention`];
        const product_display_price =
          _params[`product_${key}_display_price`] === "1";

        if (id_product) {
          const productModel = app.getProduct(id_product);
          const productData = productsDataStable.get(id_product);

          const defaultValues = productDefaults[key - 1];

          // Determine image: use image_product if exists and not "0", otherwise use static image by index
          let imageId: string;
          let staticImageSrc: string | undefined;

          if (image_product && image_product !== "0") {
            imageId = image_product;
          } else {
            // Use static image from defaults
            staticImageSrc = defaultValues.image;
            imageId = "-1"; // Use -1 as placeholder when using static image
          }

          // Determine colors: use values from params if exists, otherwise use defaults
          const finalColor = color || defaultValues.color;
          const finalDescriptionColor =
            descriptionColor || defaultValues.descriptionColor;

          _list.push({
            id_product: id_product,
            imageId: imageId,
            staticImageSrc: staticImageSrc,
            title: name_product || productModel?.get("name") || "",
            description:
              description_product ||
              productModel?.getDescription() ||
              productData?.description ||
              "",
            composition: composition_product || "",
            price: productModel?.formatPrice(),
            priceSubtitle:
              productModel && productModel.getQuantity() >= 1
                ? productModel.displayPieces()
                : undefined,
            color: finalColor,
            descriptionColor: finalDescriptionColor,
            actionType: actionType || "add_to_cart",
            link,
            displayFromMention:
              displayFromMentionRaw === "1" ? "À partir de" : undefined,
            displayPrice: product_display_price,
            compactComposition: defaultValues.compactComposition,
          });
        }
      });

      return _list;
      // Only depend on params, productIds, and stable products data
      // This prevents re-renders when cart changes (products array reference changes)
    }, [params, productIds, app, productsDataStable]);

    const renderProduct = useCallback(
      (data: IInnovationProductPetitPrinceProps, index: number) => {
        return (
          <ProductItemWithHandler
            key={data.product.id_product + "-" + index}
            {...data}
            index={index}
          />
        );
      },
      []
    );

    return (
      <LandingInnovationPetitPrince<InnovationProductAdditionalData>
        products={preparePetitPrinceProductList}
        renderProduct={renderProduct}
      />
    );
  });

export default React.memo(InnovationPetitPrince);
